import { Link } from 'react-router-dom'
import { BookMarked, CheckCircle2, ExternalLink, Star, Zap } from 'lucide-react'
import { useAuthStore, selectUser } from '../stores/authStore'
import { useStartCheckout, useOpenPortal } from '../hooks/useBilling'
import { extractErrorMessage } from '../api/client'
import { useLocale } from '../i18n/context'

export default function ProUpgradePage() {
  const { tr } = useLocale()
  const pu = tr.proUpgrade
  const user = useAuthStore(selectUser)
  const isPro = user?.plan === 'pro'
  const checkout = useStartCheckout()
  const portal = useOpenPortal()

  const FEATURE_ICONS = [
    <Star size={20} color="#F59E0B" />,
    <BookMarked size={20} color="var(--accent)" />,
    <Zap size={20} color="var(--mastery-5)" />,
  ]

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 40 }}>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <div style={{ textAlign: 'center', padding: '12px 0 0' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 14px', borderRadius: 50,
          background: 'linear-gradient(135deg, #F59E0B, #D97706)',
          color: '#fff', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em',
          marginBottom: 16,
        }}>
          <Star size={12} strokeWidth={2.5} />
          {pu.badge}
        </div>

        <h1 style={{ margin: '0 0 12px', fontSize: 32, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em', lineHeight: 1.2 }}>
          {pu.title}
        </h1>
        <p style={{ margin: 0, fontSize: 16, color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: 480, marginLeft: 'auto', marginRight: 'auto' }}>
          {pu.subtitle}
        </p>
      </div>

      {/* ── Feature cards ─────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
        {pu.features.map(({ title, description }, i) => (
          <div
            key={title}
            style={{
              backgroundColor: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 16,
              padding: 20,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              boxShadow: 'var(--shadow-card)',
            }}
          >
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              backgroundColor: 'var(--bg-secondary)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {FEATURE_ICONS[i]}
            </div>
            <div>
              <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
                {title}
              </h3>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Pricing card ──────────────────────────────────────────────────── */}
      <div style={{
        backgroundColor: 'var(--bg-surface)',
        border: '2px solid var(--accent)',
        borderRadius: 24,
        padding: 36,
        boxShadow: 'var(--shadow-float)',
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
      }}>
        {/* Price */}
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
            color: '#D97706', marginBottom: 8, textTransform: 'uppercase',
          }}>
            {pu.planLabel}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 4 }}>
            <span style={{ fontSize: 42, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.03em' }}>
              $5
            </span>
            <span style={{ fontSize: 16, color: 'var(--text-secondary)' }}>{pu.perMonth}</span>
          </div>
        </div>

        {/* Checklist */}
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {pu.checklist.map((feat) => (
            <li
              key={feat}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: 'var(--text-primary)' }}
            >
              <CheckCircle2 size={17} color="var(--success)" style={{ flexShrink: 0, marginTop: 1 }} />
              {feat}
            </li>
          ))}
        </ul>

        {/* CTA */}
        {isPro ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{
              padding: '14px 24px',
              borderRadius: 50,
              border: '1.5px solid var(--success)',
              backgroundColor: 'rgba(39,174,96,0.08)',
              textAlign: 'center',
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--success)',
            }}>
              {pu.alreadyPro}
            </div>
            <button
              onClick={() => portal.mutate()}
              disabled={portal.isPending}
              style={{
                padding: '11px 24px',
                borderRadius: 50,
                border: '1.5px solid var(--border)',
                background: 'transparent',
                color: 'var(--text-secondary)',
                fontSize: 13,
                fontWeight: 600,
                cursor: portal.isPending ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-ui)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                opacity: portal.isPending ? 0.6 : 1,
              }}
            >
              <ExternalLink size={14} />
              {portal.isPending ? pu.openingPortal : pu.manageSubBtn}
            </button>
            {portal.isError && (
              <p style={{ margin: 0, textAlign: 'center', fontSize: 12, color: 'var(--danger)' }}>
                {extractErrorMessage(portal.error, 'Could not open the portal. Please try again.')}
              </p>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              onClick={() => checkout.mutate()}
              disabled={checkout.isPending}
              style={{
                padding: '15px 24px',
                borderRadius: 50,
                border: 'none',
                background: checkout.isPending
                  ? 'var(--bg-secondary)'
                  : 'linear-gradient(135deg, #F59E0B, #D97706)',
                color: checkout.isPending ? 'var(--text-secondary)' : '#fff',
                fontSize: 15,
                fontWeight: 700,
                cursor: checkout.isPending ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-ui)',
                letterSpacing: '-0.01em',
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={(e) => { if (!checkout.isPending) e.currentTarget.style.opacity = '0.88' }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1' }}
            >
              {checkout.isPending ? pu.redirecting : pu.getCta}
            </button>
            {checkout.isError && (
              <p style={{ margin: 0, textAlign: 'center', fontSize: 12, color: 'var(--danger)' }}>
                {extractErrorMessage(checkout.error, 'Could not start checkout. Please try again.')}
              </p>
            )}
          </div>
        )}

        <p style={{ margin: 0, textAlign: 'center', fontSize: 12, color: 'var(--text-secondary)' }}>
          {pu.cancelAnytime}
        </p>
      </div>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
          {pu.faqTitle}
        </h2>

        {pu.faq.map(({ q, a }) => (
          <div
            key={q}
            style={{
              backgroundColor: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              padding: '16px 20px',
            }}
          >
            <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{q}</p>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{a}</p>
          </div>
        ))}
      </div>

      {/* ── Bottom nav ────────────────────────────────────────────────────── */}
      {!isPro && (
        <div style={{ textAlign: 'center', paddingBottom: 8 }}>
          <Link to="/catalog" style={{ fontSize: 13, color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}>
            {pu.browseCatalog}
          </Link>
        </div>
      )}
    </div>
  )
}
